// Customer portal — PWA "Add to Home Screen" banner.
//
// A subtle, dismissible bottom banner shown on first visit:
//   • Android / desktop Chromium — offers an Install button wired to the
//     browser's captured `beforeinstallprompt` (see ./pwa.ts).
//   • iOS Safari — that event never fires, so instead we show the manual
//     Share → Add to Home Screen instruction (with the iOS Share glyph).
//
// Dismiss is remembered in localStorage (a UI preference — permitted per
// [[feedback-localstorage-rule]]), and nothing shows when already installed
// (running standalone). The always-available equivalent lives in
// Account & settings, so a customer who dismisses this can still install later.

import {
  canPromptInstall,
  detectPlatform,
  isIosSafari,
  isStandalone,
  onInstallAvailable,
  onInstalled,
  promptInstall,
} from "./pwa";

const DISMISS_KEY = "kos_pwa_install_dismissed";
const STYLE_ID = "kos-pwa-banner-style";

// iOS Share glyph — custom inline SVG (1.5px stroke, 24×24), no icon libs.
const SHARE_GLYPH = `<svg class="kos-pwa-share" viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M12 15V4"/><path d="m8.5 7.5 3.5-3.5 3.5 3.5"/><path d="M6.5 11H6a1.5 1.5 0 0 0-1.5 1.5V19A1.5 1.5 0 0 0 6 20.5h12A1.5 1.5 0 0 0 19.5 19v-6.5A1.5 1.5 0 0 0 18 11h-.5"/></svg>`;

function injectStyle(): void {
  if (document.getElementById(STYLE_ID)) return;
  const style = document.createElement("style");
  style.id = STYLE_ID;
  style.textContent = `
    .kos-pwa-banner {
      position: fixed; left: 50%; transform: translateX(-50%) translateY(140%);
      bottom: calc(12px + env(safe-area-inset-bottom, 0px)); z-index: 2147483000;
      width: min(440px, calc(100vw - 24px)); display: flex; align-items: center; gap: 12px;
      padding: 12px 14px; border-radius: 14px;
      background: #161616; border: 1px solid rgba(255,255,255,0.10);
      box-shadow: 0 12px 40px rgba(0,0,0,0.55); color: #e5e7eb;
      font: 500 0.85rem/1.4 -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
      transition: transform 0.28s cubic-bezier(0.4,0,0.2,1);
    }
    .kos-pwa-banner.is-in { transform: translateX(-50%) translateY(0); }
    .kos-pwa-banner__mark { flex: none; width: 34px; height: 34px; border-radius: 9px;
      background: #0d0d0d; display: flex; align-items: center; justify-content: center; }
    .kos-pwa-banner__mark span { color: #E8A020; font-weight: 800; font-size: 1rem; }
    .kos-pwa-banner__txt { flex: 1; min-width: 0; }
    .kos-pwa-banner__title { display: block; font-weight: 700; color: #fff; margin-bottom: 1px; }
    .kos-pwa-banner__txt .kos-pwa-share { vertical-align: -2px; margin: 0 1px; }
    .kos-pwa-banner__txt b { color: #fff; font-weight: 700; }
    .kos-pwa-banner__btns { display: flex; gap: 6px; flex: none; }
    .kos-pwa-banner__btn { border: 0; border-radius: 9px; padding: 0.5rem 0.85rem;
      font-size: 0.8rem; font-weight: 700; cursor: pointer; }
    .kos-pwa-banner__btn--install { background: #E8A020; color: #1a1206; }
    .kos-pwa-banner__btn--dismiss { background: transparent; color: #9ca3af;
      border: 1px solid rgba(255,255,255,0.12); }
  `;
  document.head.appendChild(style);
}

export function mountInstallBanner(): void {
  if (typeof window === "undefined") return;
  // Already installed (running standalone) — nothing to offer.
  if (isStandalone()) return;
  try { if (localStorage.getItem(DISMISS_KEY) === "1") return; } catch { /* ignore */ }

  let banner: HTMLElement | null = null;
  let offAvailable: (() => void) | null = null;

  function remove(): void {
    offAvailable?.();
    offAvailable = null;
    if (!banner) return;
    banner.classList.remove("is-in");
    const el = banner;
    window.setTimeout(() => el.remove(), 300);
    banner = null;
  }

  function dismiss(): void {
    try { localStorage.setItem(DISMISS_KEY, "1"); } catch { /* ignore */ }
    remove();
  }

  function render(bodyHtml: string, showInstall: boolean): void {
    if (banner) return;
    injectStyle();
    banner = document.createElement("div");
    banner.className = "kos-pwa-banner";
    banner.setAttribute("role", "dialog");
    banner.setAttribute("aria-label", "Add to home screen");
    const installBtn = showInstall
      ? `<button type="button" class="kos-pwa-banner__btn kos-pwa-banner__btn--install" data-act="install">Install</button>`
      : "";
    banner.innerHTML = `
      <span class="kos-pwa-banner__mark" aria-hidden="true"><span>os</span></span>
      <span class="kos-pwa-banner__txt">${bodyHtml}</span>
      <div class="kos-pwa-banner__btns">
        ${installBtn}
        <button type="button" class="kos-pwa-banner__btn kos-pwa-banner__btn--dismiss" data-act="dismiss">${showInstall ? "Dismiss" : "Got it"}</button>
      </div>`;
    document.body.appendChild(banner);
    requestAnimationFrame(() => banner?.classList.add("is-in"));

    banner.querySelector('[data-act="install"]')?.addEventListener("click", () => {
      void (async () => {
        await promptInstall();
        remove();
      })();
    });
    banner.querySelector('[data-act="dismiss"]')?.addEventListener("click", dismiss);
  }

  function showAndroidBanner(): void {
    render("Add to your home screen for the best experience", true);
  }

  function showIosBanner(): void {
    render(
      `<span class="kos-pwa-banner__title">Add to your Home Screen</span>` +
        `Tap ${SHARE_GLYPH} then <b>Add to Home Screen</b>`,
      false,
    );
  }

  const platform = detectPlatform();

  if (platform === "ios") {
    // Only Safari's Share sheet reliably offers Add to Home Screen; showing the
    // steps in a browser that can't do it would be a dead end.
    if (isIosSafari()) showIosBanner();
    return;
  }

  // Android / desktop Chromium — show as soon as the native prompt is captured.
  // If it never fires (already installed, unsupported browser), nothing shows.
  if (canPromptInstall()) {
    showAndroidBanner();
  } else {
    offAvailable = onInstallAvailable(() => {
      offAvailable?.();
      offAvailable = null;
      showAndroidBanner();
    });
  }

  // If the app gets installed (via our prompt or the browser menu), clear the banner.
  onInstalled(() => {
    try { localStorage.setItem(DISMISS_KEY, "1"); } catch { /* ignore */ }
    remove();
  });
}
